import { z } from "zod";
import { evidenceAttachmentSchema } from "./contribution-evidence.js";

/**
 * US-E19.F1-0200-S01 (@UC9296/@UC9297) — CONTRACT luồng SỬA-NHẦM (correction) 🔴 money.
 *
 * "Sửa" bút toán KHÔNG BAO GIỜ là UPDATE/DELETE sổ cái (append-only B2/G2). Sửa =
 *   REVERSAL(bút toán gốc) + POST(bút toán ĐÚNG) — cả 2 trong 1 transaction.
 * Contract này mô tả yêu cầu sửa + kết quả (3 con trỏ: gốc · reversal · đúng).
 *
 * B1 (no-float): mọi tiền = **STRING** minor (BigInt serialize an toàn qua HTTP), regex CHỈ chữ số ⇒
 * `500.0` / number JS bị từ chối NGAY Ở BIÊN (không lọt vào service dưới dạng float).
 * `.strict()` mọi object ⇒ field lạ bị từ chối (không leak field không kiểm soát vào tầng tiền).
 */

/** Chỉ chữ số (minor units) — KHÔNG dấu chấm/phẩy/âm. */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Trần PG signed BIGINT (chống overflow P2020 → 500). */
const PG_BIGINT_MAX = 9223372036854775807n;

/** Trần độ dài khoá nghiệp vụ (đồng bộ `deriveBusinessIdempotencyKey` — domain S02). */
export const BUSINESS_IDEMPOTENCY_KEY_MAX = 200;

/** Khoá idempotency NGHIỆP VỤ (ổn định theo hành động — KHÔNG random mỗi lần bấm). */
export const businessIdempotencyKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(BUSINESS_IDEMPOTENCY_KEY_MAX);

/**
 * Tiền minor STRING dương ≤ trần BIGINT (B1).
 *
 * 🔴 `export` để lane admin (`admin-expenses.ts`) DÙNG LẠI, không chép phép kiểm tiền thứ hai
 * (§3.8). Hai bản regex/refine cho "số tiền hợp lệ" sẽ trôi khỏi nhau, và lúc trôi thì một đường
 * ghi nhận số mà đường kia từ chối — không ai biết bên nào đúng.
 */
export const positiveMinorString = z
  .string()
  .trim()
  .regex(MONEY_INTEGER_STRING_PATTERN, "Số tiền phải là số nguyên (đơn vị nhỏ nhất)")
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, "Số tiền phải lớn hơn 0 và không vượt giới hạn");

/** Lý do sửa — BẮT BUỘC (dấu vết audit "ai sửa, vì sao"). Rỗng/toàn khoảng trắng ⇒ từ chối. */
export const CORRECTION_REASON_MAX = 500;

/** Giá trị ĐÚNG sau khi sửa: số tiền (bắt buộc) + người nhận đúng (khi sửa NHẦM NGƯỜI). */
export const correctedValueSchema = z
  .object({
    amountMinor: positiveMinorString,
    /**
     * Sửa NHẦM NGƯỜI — PHẠM VI HẸP (CORRECTION-PAYEE-ON-DUE · FIX-1345, QĐ PO 09/10 vòng 23 + 24). Vắng ⇒ giữ người của bút toán gốc.
     *  · Tài trợ của THÀNH VIÊN (`SPONSORSHIP_MEMBER`): ĐỔI được — người mới qua cùng vị-từ ghế của ghi thu; hiện công khai ở lịch sử sửa và báo người
     *    cũ. Trừ tài trợ đến từ lời báo góp quỹ chung đã duyệt ⇒ 422.
     *  · Bút toán gắn KHOẢN THU (đóng góp · nộp hộ): chỉ nhận CHÍNH người của khoản (không đổi gì); người khác ⇒ 422 — lối ra hai bước: huỷ ghi
     *    nhận + ghi nhận khoản đúng.
     *  · Bút toán không gắn người (chi · thu khác · tài trợ ngoài): gửi trường này ⇒ 422.
     * Mọi 422 trên mang `LEDGER_CORRECTION_PAYEE_CHANGE_NOT_SUPPORTED`, 0 bút toán. Server chuẩn hoá chữ thường.
     */
    payeeMemberId: z.string().uuid().optional(),
  })
  .strict();

/** POST /v1/funds/:id/ledger-corrections — yêu cầu sửa 1 bút toán ghi nhầm. */
export const correctionRequestSchema = z
  .object({
    /** Bút toán NHẦM cần đảo (phải thuộc đúng quỹ — service chốt tenant, sai ⇒ 404). */
    targetTxId: z.string().uuid(),
    /** Khoá nghiệp vụ chống bấm kép: 2 lần cùng key ⇒ ĐÚNG 1 reversal + 1 post (không nhân đôi). */
    correctionKey: businessIdempotencyKeySchema,
    reason: z.string().trim().min(1).max(CORRECTION_REASON_MAX),
    corrected: correctedValueSchema,
  })
  .strict();

export type CorrectionRequest = z.infer<typeof correctionRequestSchema>;
export type CorrectedValue = z.infer<typeof correctedValueSchema>;

/** Kết quả sửa: 3 con trỏ bút toán (gốc BẤT BIẾN · reversal · đúng) + số ĐÚNG (string minor). */
export const correctionResponseSchema = z
  .object({
    targetTxId: z.string().uuid(),
    reversalTxId: z.string().uuid(),
    correctedTxId: z.string().uuid(),
    correctedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    reason: z.string(),
    correctedAt: z.string().datetime(),
  })
  .strict();

export type CorrectionResponse = z.infer<typeof correctionResponseSchema>;

/**
 * 🔴🔴 US-E14.F1-0848 (@UC9190 @UC9297 · QĐ-D người dùng 23/09/2026) — ĐƯỜNG ĐỌC LỊCH SỬ CHỈNH SỬA.
 *
 * Bắt người dùng nhập một trường `reason` BẮT BUỘC rồi không cho ai xem lại là vô nghĩa. Trước task
 * này `reason` CÓ lưu (memo hai bút toán + `audit_log`) nhưng **0 endpoint đọc ngược** ⇒ end-user
 * không có cửa nào.
 *
 * 🔴 KHÔNG bảng mới, KHÔNG parse ngược memo: `audit_log` (`transaction_corrected`) đã mang đủ
 * `reason` · `correctedAmountMinor` · mốc · người sửa. Memo là chữ tự do — đọc số tiền từ một chuỗi
 * người gõ là đúng thứ §3.1 cấm.
 *
 * Áp cho CẢ Thu lẫn Chi (cùng một `audit_log`, cùng một endpoint — không cơ chế thứ hai).
 */
/**
 * 🔴 FIX-1345 (QĐ PO 09/10 vòng 24 "Hiện công khai") — lần sửa ĐỔI NGƯỜI TÀI TRỢ thành viên: người cũ → người mới (id ghế + tên hiển thị theo luật
 * tên chung). Đọc từ SỔ (bút toán gốc ↔ bản ghi-lại). Tên `null` = không resolve được (đã rời / ẩn danh / ghế ngoài nhóm) — KHÔNG bịa tên.
 * `previousMemberId` `null` = bút toán ghi trước khi sổ lưu người đóng góp (KHÔNG rõ là ai — khác "đã rời": id có mà tên `null`).
 *
 * 🔴🔴 FIX-1345b (review #1345 vòng 2 · §7 P5.3 kênh XEM) — discriminated union theo `redacted`: "công khai" = công khai TRONG luật xem sẵn có
 * (`shouldHidePerson`, một nguồn với feed). Người xem không phải O/A/T ở quỹ chưa mở cả hai trục (tiền `public` + `all_members`) ⇒ `{ redacted: true }`:
 * vẫn biết CÓ đổi người, không tên, không id (trạng thái "ẩn mà vẫn mang tên" không biểu diễn được).
 */
const correctionSponsorChangeVisibleSchema = z
  .object({
    redacted: z.literal(false),
    previousMemberId: z.string().uuid().nullable(),
    previousName: z.string().nullable(),
    correctedMemberId: z.string().uuid(),
    correctedName: z.string().nullable(),
  })
  .strict();

const correctionSponsorChangeRedactedSchema = z.object({ redacted: z.literal(true) }).strict();

export const correctionSponsorChangeSchema = z.discriminatedUnion("redacted", [
  correctionSponsorChangeVisibleSchema,
  correctionSponsorChangeRedactedSchema,
]);

export type CorrectionSponsorChange = z.infer<typeof correctionSponsorChangeSchema>;

export const correctionHistoryEntrySchema = z
  .object({
    /** Bút toán bị sửa ở lần này (bản GỐC của lượt đó — vẫn còn nguyên trong sổ). */
    targetTxId: z.string().uuid(),
    /** Bút toán ĐÚNG sinh ra ở lần này — cũng là đích của lần sửa kế tiếp (nếu có). */
    correctedTxId: z.string().uuid(),
    /** Số TRƯỚC lần sửa. `null` khi bút toán gốc không còn đọc được số (dữ liệu cũ). */
    previousAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).nullable(),
    /** Số SAU lần sửa — luôn có (audit ghi tường minh lúc sửa). */
    correctedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /**
     * Đơn vị tiền của lần sửa. `null` ĐÚNG KHI `previousAmountMinor` là `null` — cả hai đọc từ
     * bút toán gốc, nên mất bút toán là mất cả hai.
     *
     * 🔴 KHÔNG dùng `""` làm giá trị vắng: `.length(3)` từ chối chuỗi rỗng ⇒ một hàng lịch sử
     * thiếu bút toán gốc làm **sập cả request 500** thay vì hiện một dòng khuyết. Hai trường cùng
     * nguồn thì phải cùng cách chịu thiếu — `previousAmountMinor` đã `nullable`, trường này lẽ ra
     * cũng vậy (review PR #931 · HIGH-1).
     */
    currency: z.string().length(3).nullable(),
    reason: z.string(),
    correctedAt: z.string().datetime(),
    /**
     * TÊN người sửa để hiển thị. `null` khi không resolve được (người đã rời nhóm / đã ẩn danh
     * GDPR) — KHÔNG bịa tên, cũng không phát userId ra ngoài.
     */
    correctedByName: z.string().nullable(),
    /**
     * 🔴 US-AE4.F7-1007 (@UC9951) — lần sửa do NHÂN VIÊN Goppy làm (admin "Sửa thu" / sửa khoản chi), không phải thành viên.
     * Khi `true`, `correctedByName` LUÔN `null` (không phát tên nhân viên ra nhóm) và client in nhãn "Đội hỗ trợ Goppy".
     * Thiếu cờ này thì tên null bị hiểu là "Người đã rời nhóm" — nói SAI ai đã đổi tiền của người ta.
     */
    correctedBySupport: z.boolean(),
    /** FIX-1345 — đổi người tài trợ ở lần sửa này; `null` = lần sửa không đổi người (chỉ sửa số, hoặc không phải tài trợ thành viên). */
    sponsorChange: correctionSponsorChangeSchema.nullable(),
  })
  .strict();

/**
 * 🔴 US-AE4.F7-1039 (@UC9951 · QĐ người dùng 30/10/2026) — một lần ĐỔI TỆP ĐÍNH KÈM của khoản (0 bút toán). Nguồn = `audit_log`
 * `transaction_attachment_changed`, như lần sửa số tiền. Link tệp ký ngắn hạn, CÙNG cổng với tệp của màn chi tiết: vai không được
 * xem tệp ⇒ `previous`/`current` null và `attachmentsHidden: true` (vẫn biết là có đổi, không thấy tệp).
 */
export const correctionAttachmentChangeSchema = z
  .object({
    /** Bút toán được đổi tệp (một mắt trong chuỗi sửa — có thể là bút toán đã bị sửa số sau đó). */
    txId: z.string().uuid(),
    /** Tệp TRƯỚC lần đổi; `null` khi trước đó chưa có tệp (hoặc bị ẩn). */
    previous: evidenceAttachmentSchema.nullable(),
    /** Tệp SAU lần đổi; `null` chỉ khi bị ẩn. */
    current: evidenceAttachmentSchema.nullable(),
    attachmentsHidden: z.boolean(),
    reason: z.string(),
    changedAt: z.string().datetime(),
    /** Như `correctedByName` — `null` khi không resolve được hoặc là nhân viên. */
    changedByName: z.string().nullable(),
    /** Như `correctedBySupport` — nhân viên Goppy đổi ⇒ "Đội hỗ trợ Goppy". */
    changedBySupport: z.boolean(),
  })
  .strict();

/** `GET /v1/funds/:id/transactions/:txId/correction-history` — mới nhất TRƯỚC. */
export const correctionHistoryResponseSchema = z
  .object({
    txId: z.string().uuid(),
    /** Rỗng ⇒ bút toán CHƯA TỪNG bị sửa ⇒ client **ẩn hẳn** khối, không in khung rỗng. */
    entries: z.array(correctionHistoryEntrySchema),
    /**
     * 🔴 US-AE4.F7-1039 — các lần đổi tệp trên CẢ chuỗi sửa, mới nhất TRƯỚC. Mảng riêng (không trộn vào `entries`) để client cũ —
     * vốn đọc `entries` với số tiền bắt buộc — không vỡ; client mới tự trộn hai mảng theo thời gian.
     */
    attachmentChanges: z.array(correctionAttachmentChangeSchema),
  })
  .strict();

export type CorrectionAttachmentChange = z.infer<typeof correctionAttachmentChangeSchema>;
export type CorrectionHistoryEntry = z.infer<typeof correctionHistoryEntrySchema>;
export type CorrectionHistoryResponse = z.infer<typeof correctionHistoryResponseSchema>;

/**
 * 🔴🔴 REVERSE-OF-REVERSAL-BLOCK (@UC9104 @UC0040) — MÃ LỖI ỔN ĐỊNH khi một cửa xin ĐẢO một bút toán mà
 * bút toán đó CHÍNH LÀ một bản đảo (`txType='REVERSAL'`).
 *
 * Partial unique `WHERE txType='REVERSAL'` chỉ cấm đảo CÙNG một gốc hai lần; R2 đảo R1 là một gốc
 * KHÁC nên index để lọt ⇒ bút toán gốc "sống lại" trong sổ mà không có người ghi lại nó (CLAUDE.md
 * §3.3: *"cho đảo tiếp = đẻ tiền"*). Muốn khôi phục một khoản đã bị đảo nhầm ⇒ GHI MỚI bút toán đúng
 * (POST), không đảo bản đảo.
 *
 * Phát ra tại `LedgerService.reverse` (chỗ GHI — mọi cửa đảo đi qua) với HTTP **409**; các cửa ngoài
 * (admin-corrections create, dispute-remediation) dùng chung hàm chốt nên trả CÙNG mã. Ngôn-ngữ-trung-
 * tính: client map `code → message`, không đọc `detail`.
 */
export const LEDGER_REVERSAL_NOT_REVERSIBLE_CODE = "LEDGER_REVERSAL_NOT_REVERSIBLE" as const;

/**
 * 🔴🔴 CORRECTION-REVERSE-TOCTOU (@UC9297 @UC0036) — MÃ LỖI ỔN ĐỊNH khi đường SỬA NHẦM xin đảo một bút toán ĐÃ bị đảo (bởi lần sửa
 * khác, lần huỷ khoản chi, lần khắc phục…). 409, 0 bút toán. Phát ra ở bước kiểm của `CorrectionService` VÀ ở chỗ GHI
 * (`LedgerService.reverse({ requireFresh: true })` — gặp bản đảo đã có thì ném thay vì trả lặng bản cũ), vì một lần đảo khác có thể
 * commit lọt vào KHE giữa hai bước: trả lặng bản cũ ở đó = post thêm một bút toán "đúng" cho một lần đảo (§3.3 "đẻ tiền").
 * Ngôn-ngữ-trung-tính: client map `code → message`.
 */
export const LEDGER_TX_ALREADY_REVERSED_CODE = "LEDGER_TX_ALREADY_REVERSED" as const;

/**
 * 🔴🔴 LEDGER-POST-REJECT-REVERSAL-TXTYPE (@UC0040 @UC9104) — MÃ LỖI ỔN ĐỊNH khi có ai xin GHI MỚI một bút
 * toán `txType='REVERSAL'` qua `LedgerService.post` (đường ghi thường, không có gốc).
 *
 * `post()` không nhận `reversesTxId` ⇒ REVERSAL qua đó LUÔN mồ côi: lách partial unique `WHERE
 * txType='REVERSAL'` (NULL ≠ NULL), lách `LEDGER_REVERSAL_NOT_REVERSIBLE`, làm sai vị từ "đã bị đảo". Bản
 * đảo chỉ sinh ra ở `LedgerService.reverse` (đọc gốc, kiểm, trỏ tới gốc).
 *
 * HTTP **422** (không 409): yêu cầu SAI HÌNH DẠNG — một bản đảo thiếu gốc — chứ không xung đột trạng thái sổ.
 * Hôm nay 0 cửa nào cho người ngoài chọn txType (B0 hồ sơ LEDGER-POST-REJECT-REVERSAL-TXTYPE) ⇒ mã này là
 * chốt phòng thủ; nếu nó lộ ra client thì đó là một lỗi lập trình cần sửa ở caller.
 */
export const LEDGER_REVERSAL_ONLY_VIA_REVERSE_CODE = "LEDGER_REVERSAL_ONLY_VIA_REVERSE" as const;

/**
 * 🔴 LEDGER-POST-GUARD-LOWS (@UC0040, review #1162 L2) — MÃ LỖI ỔN ĐỊNH khi `LedgerService.post` nhận một `txType` KHÔNG
 * có trong whitelist `TX_TYPES` (sai hoa/thường, khoảng trắng, chuỗi lạ). Tách khỏi `LEDGER_REVERSAL_ONLY_VIA_REVERSE`
 * (nhánh `REVERSAL` đi nhầm cửa — giữ nguyên mã đó): "loại không tồn tại" và "bản đảo thiếu gốc" là hai lỗi khác nhau.
 *
 * HTTP **422**, 0 bút toán (chốt đứng ở bước 0 của `post()`, trước khi tạo account/khoá). Hôm nay không đường người
 * dùng nào chạm nhánh này — mã là chốt phòng thủ; lộ ra client = lỗi lập trình ở caller.
 */
export const LEDGER_TXTYPE_NOT_POSTABLE_CODE = "LEDGER_TXTYPE_NOT_POSTABLE" as const;

/**
 * 🔴 FIX-1241b B2 (review #1241 vòng 2 · @UC0036 @UC9297) — các loại bút toán đường SỬA NHẦM (`POST /v1/funds/:id/ledger-corrections`, và hai cửa
 * admin dùng chung `CorrectionService.correct`) được phép sửa số. DANH SÁCH ĐÓNG, cùng khuôn `ADMIN_INCOME_CORRECTABLE_TX_TYPES`: những loại
 * màn "Chi tiết hoạt động" mở form sửa (nộp quỹ · khoản chi · đóng góp thêm — `RecordIncomeService` ghi ba loại thu) + `PAY_ON_BEHALF` (QĐ PO
 * 09/08/2026 REVIEW B8 H1: sửa CON SỐ, giữ quan hệ trả hộ — `_syncMemberDue` giữ `paid_on_behalf`; khoản chi ghi qua nộp hộ — admin sửa số
 * khoản chi AC8d). Loại khác chưa từng có đường sửa được đo, và sửa chúng bằng đảo + ghi lại là sai hình:
 *  · `ADVANCE_CREDIT` — ứng trước là XÔ của sổ nợ (`debt_entries`); đảo + ghi lại ở sổ cái mà không đụng xô ⇒ xô lệch sổ cái;
 *  · `OFFSET` — cấn trừ đóng một khoản bằng ứng trước; sửa số ⇒ khoản và xô cùng sai;
 *  · vay/trả nợ/chuyển liên sổ… — mỗi loại có bảng nguồn riêng (khoản vay, nợ người↔người, chân đối ứng ở sổ kia) mà đường sửa không đồng bộ.
 * Thêm một loại vào đây = phải có đồng bộ nguồn tương ứng trong `CorrectionService` + test kết quả.
 *
 * 🔴 CORRECTION-SOURCE-SYNC — danh sách này là cổng THỨ NHẤT (hình dạng loại). Cổng THỨ HAI xét NƠI GHI (`LEDGER_TX_MANAGED_BY_SOURCE_CODE`
 * dưới): cùng một loại trong danh sách vẫn bị chặn khi do module có bảng nguồn ghi. `PAY_ON_BEHALF` vẫn sửa được (QĐ PO 09/08 H1, xác nhận
 * lại 08/10 vòng 16); đồng bộ nợ `on_behalf` khi sửa = task POB-CORRECTION-DEBT-SYNC.
 */
export const LEDGER_CORRECTABLE_TX_TYPES = [
  "CONTRIBUTION",
  "EXPENSE",
  "SPONSORSHIP_MEMBER",
  "SPONSORSHIP_EXTERNAL",
  "OTHER_INCOME",
  "PAY_ON_BEHALF",
] as const;
export type LedgerCorrectableTxType = (typeof LEDGER_CORRECTABLE_TX_TYPES)[number];

/**
 * 🔴 FIX-1241b B2 — MÃ LỖI ỔN ĐỊNH khi đường sửa nhầm nhận một bút toán ngoài `LEDGER_CORRECTABLE_TX_TYPES`. HTTP **422**, 0 bút toán (chốt
 * đứng trước mọi khoá/ghi). `params.txType` = loại bút toán bị từ chối. Ngôn-ngữ-trung-tính: client map `code → message`.
 */
export const LEDGER_TX_NOT_CORRECTABLE_CODE = "LEDGER_TX_NOT_CORRECTABLE" as const;

/**
 * 🔴 CORRECTION-SOURCE-SYNC (§8 review #1241 vòng 3 · @UC9297 @UC0036) — NƠI GHI của một bút toán mà đường SỬA NHẦM không đồng bộ được.
 *
 * `LEDGER_CORRECTABLE_TX_TYPES` xét theo LOẠI bút toán; nhưng cùng một loại (`OTHER_INCOME`, `EXPENSE`, …) còn được các module có BẢNG
 * NGUỒN riêng ghi (hiện vật, hiếu hỉ, đầu tư, gói mua chung, phân bổ phí, chi ngoại tệ…). Đảo + ghi lại một bút toán như thế
 * mà không chạm bảng nguồn ⇒ sổ cái nói một số, bảng nguồn nói số khác. Phân loại (một hàm duy nhất, TẤT ĐỊNH — không đọc memo):
 * `apps/api/src/modules/ledger/correction-source.ts`. Tên ở đây là tên MODULE trung tính (không id, không thuật ngữ kế toán) — client
 * map `code → message`, KHÔNG in thẳng giá trị này lên màn hình. `unknown` = nơi ghi không xác định được tất định ⇒ chặn (fail-closed).
 */
export const LEDGER_TX_SOURCES = [
  "in_kind",
  "gift",
  "invest",
  "subscription_pool",
  "fee_allocation",
  "fx_spend",
  "deposit",
  "shared_asset",
  "debt_settlement",
  "hui",
  "unknown",
] as const;
export type LedgerTxSource = (typeof LEDGER_TX_SOURCES)[number];

/**
 * 🔴 CORRECTION-SOURCE-SYNC — MÃ LỖI ỔN ĐỊNH khi đường sửa nhầm nhận một bút toán do module có bảng nguồn ghi. HTTP **422**, 0 bút toán
 * (chốt đứng trước mọi khoá/ghi). `params.source` ∈ `LEDGER_TX_SOURCES`. Thu hẹp (không nới gì): muốn sửa thì sửa ở màn của module đó.
 */
export const LEDGER_TX_MANAGED_BY_SOURCE_CODE = "LEDGER_TX_MANAGED_BY_SOURCE" as const;

/**
 * 🔴 FEED-CORRECTABLE-FLAG (@UC9297 @UC0036 · §8 #1333) — VÌ SAO một bút toán KHÔNG sửa nhầm được, trả ở đường ĐỌC (chi tiết khoản chi · nhãn bút
 * toán) cùng `correctable` để màn chi tiết hoạt động ẩn nút sửa TRƯỚC khi bấm, thay vì để người dùng bấm rồi nhận 422/409. Tính bằng CÙNG luật thuần
 * với cổng ghi (`apps/api/src/modules/ledger/correction-eligibility.ts` — `ledgerCorrectionBlockReason`); lưới int-spec đối chiếu từng lý do với
 * phản hồi thật của `POST …/ledger-corrections`. Mã trung tính — client map `reason → câu`, không in thẳng.
 *  · `forbidden` — vai người xem không phải O/A/T (cổng ghi 403).
 *  · `no_active_tx` — khoản chi chưa có bút toán đang hiệu lực (chưa ghi sổ / đã đảo mà chưa ghi lại) — không có đích để sửa.
 *  · `reversal` — chính là bản ĐẢO (422). · `tx_type_not_correctable` — loại ngoài `LEDGER_CORRECTABLE_TX_TYPES` (422 `LEDGER_TX_NOT_CORRECTABLE`).
 *  · `managed_by_source` — do module có bảng nguồn ghi (422 `LEDGER_TX_MANAGED_BY_SOURCE`).
 *  · `expense_has_fund_debt` — khoản chi khẩn cấp đã thành nợ quỹ (409 `EXPENSE_HAS_FUND_DEBT`).
 *  · `already_reversed` — đã bị sửa/huỷ trước đó, hoặc khoản chi không còn ở trạng thái đã ghi sổ (409 `LEDGER_TX_ALREADY_REVERSED`).
 *  · `multi_leg` — bút toán nhiều chân (422). · `expense_split` — khoản chi có chia suất (422).
 *  · FIX-1347 M1 — `group_suspended` / `group_archived` / `fund_frozen`: nhóm đình chỉ · nhóm lưu trữ · quỹ đóng băng (409 `GROUP_SUSPENDED` /
 *    `GROUP_ARCHIVED` / `FUND_FROZEN` của guard — đứng TRƯỚC vai, nên member cũng nhận lý do này).
 *  · FIX-1347 M3 — `fund_debt_self_confirm`: NGƯỜI XEM là người nợ của khoản "nợ quỹ" và nhóm còn quản lý khác (403
 *    `FUND_DEBT_SELF_CONFIRM_FORBIDDEN`, §3.7) — cờ phụ thuộc người xem, như vai.
 *  · FIX-1347 M2 — `due_waived`: lần đóng của khoản đã được miễn (422).
 * KHÔNG phủ các chốt phụ thuộc ĐẦU VÀO của lần sửa (đổi người nộp hộ · hạn mức earmark khi sửa tăng · người nhận không thuộc quỹ · số mới vượt
 * nghĩa vụ): toast lỗi của màn vẫn là chốt cuối.
 */
export const LEDGER_CORRECTION_BLOCK_REASONS = [
  "group_suspended",
  "group_archived",
  "fund_frozen",
  "forbidden",
  "no_active_tx",
  "reversal",
  "tx_type_not_correctable",
  "managed_by_source",
  "expense_has_fund_debt",
  "already_reversed",
  "multi_leg",
  "expense_split",
  "fund_debt_self_confirm",
  "due_waived",
] as const;
export const ledgerCorrectionBlockReasonSchema = z.enum(LEDGER_CORRECTION_BLOCK_REASONS);
export type LedgerCorrectionBlockReason = (typeof LEDGER_CORRECTION_BLOCK_REASONS)[number];

/**
 * Hai trường cờ sửa nhầm ghép vào response `.strict()` của đường đọc. `correctable = true` ⇔ `notCorrectableReason = null` — server dựng cả hai từ MỘT
 * giá trị (`toLedgerCorrectability` ở api), không gán tay riêng từng trường.
 */
export const ledgerCorrectabilityShape = {
  correctable: z.boolean(),
  notCorrectableReason: ledgerCorrectionBlockReasonSchema.nullable(),
} as const;

/**
 * 🔴 POB-CORRECTION-LOWS (2) (review #1341 LOW-3 · @UC9570 @UC0062) — MÃ LỖI ỔN ĐỊNH khi sửa TĂNG một bút toán nộp hộ (nhánh ghi nợ `on_behalf`
 * MỚI người của suất B → người nộp hộ A) mà B hoặc A KHÔNG còn là suất sống của nhóm (rời / bị gỡ — `ROSTER_MEMBER_STATUSES`), hoặc nợ của cặp đã
 * được CHO QUA từ lúc nộp hộ (tha nợ người đã mất · gạch nợ — P4.3: tha là quyết định của người, máy không đẻ lại nợ đè lên). HTTP **409**, 0 bút
 * toán, 0 dòng nợ (chặn trước mọi ghi). Không `params`. Lối ra: không có trong app (liên hệ hỗ trợ). Sửa GIẢM / không đổi số không bị mã này chặn.
 */
export const PAY_ON_BEHALF_DEBT_PARTY_CLOSED_CODE = "PAY_ON_BEHALF_DEBT_PARTY_CLOSED" as const;

/**
 * 🔴 POB-CORRECTION-DEBT-SYNC (@UC9570 @UC0062 · QĐ PO 08/10 vòng 21 (a)) — MÃ LỖI ỔN ĐỊNH khi sửa GIẢM một bút toán nộp hộ mà người được nộp hộ
 * (B) đã trả lại HOẶC đã được cho qua (FIX-1341 M2 — tập trừ gồm settle · forgiven · refund; P4.3 "được tha ≠ đã trả" nên câu hiển thị TRUNG TÍNH, nói
 * cả hai) với người nộp hộ (A) nhiều hơn phần nợ còn lại sau khi giảm (nợ RÒNG CẶP B→A trong quỹ < phần giảm). HTTP **409**, 0 bút toán, 0 dòng nợ
 * (chặn trước mọi ghi). Không `params` (không lộ số nợ người↔người cho người sửa). Lối ra: không có trong app — hai người tự đối chiếu phần chênh
 * (QĐ: chặn, không máy ghi nợ ngược). Tên mã giữ nguyên (mã ổn định — client đã map).
 */
export const PAY_ON_BEHALF_DEBT_ALREADY_REPAID_CODE = "PAY_ON_BEHALF_DEBT_ALREADY_REPAID" as const;

/**
 * 🔴 FIX-1341 M1 (POB-CORRECTION-DEBT-SYNC) — MÃ LỖI ỔN ĐỊNH khi các dòng nợ nộp hộ của khoản KHÔNG thuộc đúng một cặp (người của suất → người nộp
 * hộ) đã khoá — dữ liệu lệch hoặc chen ngoài khoá. HTTP **422**, 0 ghi (fail-closed, không đoán ai nợ ai). Lối ra: đối chiếu thủ công.
 */
export const PAY_ON_BEHALF_DEBT_PAIR_MISMATCH_CODE = "PAY_ON_BEHALF_DEBT_PAIR_MISMATCH" as const;

/**
 * 🔴 FIX-1341 M3 + CORRECTION-PAYEE-ON-DUE (thu hẹp fail-closed) — MÃ LỖI ỔN ĐỊNH khi sửa nhầm một bút toán GẮN KHOẢN THU (`memberDueId` ≠ null:
 * đóng góp · nộp hộ) mà ĐỔI NGƯỜI (`payeeMemberId` khác người của khoản): chân "khoản phải thu" sẽ sang người mới trong khi khoản (và nợ nộp hộ) vẫn
 * của người cũ. HTTP **422**, 0 bút toán (chặn trước mọi khoá/ghi). Lối ra: chỉ sửa số tiền. Tài trợ của thành viên (không gắn khoản) vẫn đổi người
 * được — bản ghi-lại mang người mới.
 */
export const LEDGER_CORRECTION_PAYEE_CHANGE_NOT_SUPPORTED_CODE = "LEDGER_CORRECTION_PAYEE_CHANGE_NOT_SUPPORTED" as const;
